import {
  BorderStyle,
  ExternalHyperlink,
  Footer,
  Paragraph,
  Table,
  TableCell,
  TableLayoutType,
  TableRow,
  TextRun,
  WidthType,
} from "docx";
import { lightTheme } from "styles/theme";

import { BidNoBidResponse } from "lib/hooks/api/bid/answers/useGetBidReportAnswers";
import { normaliseMarkdownText } from "lib/utils";

const primaryColor = lightTheme.colors.sysPrimaryDefault.replace("#", "");
const placeholderColor = lightTheme.colors.sysTextPlaceholder.replace("#", "");

// A4 page width (11906 twips) minus 1-inch margins on each side (1440 twips each)
export const DOCX_PAGE_WIDTH = 9026;
export const DOCX_COL1_WIDTH = Math.round(DOCX_PAGE_WIDTH * 0.35);
export const DOCX_COL2_WIDTH = DOCX_PAGE_WIDTH - DOCX_COL1_WIDTH;
export const DOCX_META_LABEL_WIDTH = Math.round(DOCX_PAGE_WIDTH * 0.25);
export const DOCX_META_VALUE_WIDTH = DOCX_PAGE_WIDTH - DOCX_META_LABEL_WIDTH;

export const noBorder = { style: BorderStyle.NONE, size: 0, color: "auto" } as const;
export const noBorders = { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder };
export const allNoBorders = {
  ...noBorders,
  insideHorizontal: noBorder,
  insideVertical: noBorder,
};

export function markdownToPlainText(text: string): string {
  return normaliseMarkdownText(text)
    .replace(/#{1,6}\s+/gm, "")
    .replace(/\*\*(.+?)\*\*/gs, "$1")
    .replace(/\*(.+?)\*/gs, "$1")
    .replace(/`(.+?)`/g, "$1")
    .replace(/\[(.+?)\]\(.+?\)/g, "$1")
    .replace(/^[-*+]\s+/gm, "• ")
    .replace(/^\d+\.\s+/gm, "")
    .trim();
}

export function buildTextLines(text: string): TextRun[] {
  const lines = markdownToPlainText(text).split("\n");
  return lines.flatMap((line, i) =>
    i === 0 ? [new TextRun({ text: line })] : [new TextRun({ text: line, break: 1 })],
  );
}

export function buildAnswerParagraphs(
  question: BidNoBidResponse[number]["questions"][number],
): Paragraph[] {
  const rawAnswer = question.answer?.answer ?? "";
  const answerParagraph = new Paragraph({ children: buildTextLines(rawAnswer) });

  const activeSources = question.sources?.filter((s) => !s.isDeleted) ?? [];
  if (activeSources.length === 0) return [answerParagraph];

  const names = [...new Set(activeSources.map((s) => s.fileName ?? s.title ?? "Unknown"))];
  const sourcesText = `(${activeSources.length} source${activeSources.length !== 1 ? "s" : ""}: ${names.join(", ")})`;

  return [
    answerParagraph,
    new Paragraph({
      children: [new TextRun({ text: sourcesText, italics: true, color: placeholderColor })],
    }),
  ];
}

export function buildMetaRow(label: string, text: string, href?: string): TableRow {
  return new TableRow({
    children: [
      new TableCell({
        width: { size: DOCX_META_LABEL_WIDTH, type: WidthType.DXA },
        borders: noBorders,
        children: [
          new Paragraph({ children: [new TextRun({ text: label, color: placeholderColor })] }),
        ],
      }),
      new TableCell({
        width: { size: DOCX_META_VALUE_WIDTH, type: WidthType.DXA },
        borders: noBorders,
        children: [
          new Paragraph({
            children: href
              ? [
                  new ExternalHyperlink({
                    link: href,
                    children: [new TextRun({ text, color: primaryColor })],
                  }),
                ]
              : [new TextRun({ text })],
          }),
        ],
      }),
    ],
  });
}

export function buildMetaTable(rows: TableRow[]): Table {
  return new Table({
    width: { size: DOCX_PAGE_WIDTH, type: WidthType.DXA },
    layout: TableLayoutType.FIXED,
    columnWidths: [DOCX_META_LABEL_WIDTH, DOCX_META_VALUE_WIDTH],
    borders: allNoBorders,
    rows,
  });
}

export function buildQaTable(group: BidNoBidResponse[number]): Table {
  return new Table({
    width: { size: DOCX_PAGE_WIDTH, type: WidthType.DXA },
    layout: TableLayoutType.FIXED,
    columnWidths: [DOCX_COL1_WIDTH, DOCX_COL2_WIDTH],
    borders: allNoBorders,
    rows: group.questions.map(
      (question) =>
        new TableRow({
          children: [
            new TableCell({
              width: { size: DOCX_COL1_WIDTH, type: WidthType.DXA },
              borders: noBorders,
              children: [
                new Paragraph({
                  children: [new TextRun({ text: question.title, bold: true })],
                }),
              ],
            }),
            new TableCell({
              width: { size: DOCX_COL2_WIDTH, type: WidthType.DXA },
              borders: noBorders,
              children: buildAnswerParagraphs(question),
            }),
          ],
        }),
    ),
  });
}

export function buildDocxFooter(): Footer {
  return new Footer({
    children: [
      new Paragraph({
        children: [
          new TextRun({
            text: "*AI generated responses vary in accuracy – please verify important information.",
            italics: true,
            color: placeholderColor,
          }),
        ],
      }),
    ],
  });
}
